import React, { Fragment, memo } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { Button } from 'src/components/shared/Button'
import { CommonModal } from 'src/components/shared/CommonModal'

interface Props {
  isOpen: boolean
  customerName: string
  currentStatusLabel: string
  isRequesting: boolean
  onConfirm(): void
  onClose(): void
}

/**
 * Confirms before firing. `POST /business/underwriting` is an outward-facing call to a lender
 * that writes a new underwriting status, so it is not a silent one-click action. The recap names
 * the customer and the current status, because re-requesting at APPROVED is a real (and
 * occasionally intended) thing to do and the admin should see what they are replacing.
 */
const RequestUnderwritingModalComponent = ({
  isOpen,
  customerName,
  currentStatusLabel,
  isRequesting,
  onConfirm,
  onClose,
}: Props) => {
  return (
    <CommonModal
      isOpen={isOpen}
      onClose={onClose}
      title="Request underwriting?"
      buttons={
        <Fragment>
          <Button
            size="small"
            color="grey"
            onClick={onClose}
            disabled={isRequesting}
            data-testid="request-underwriting-cancel"
          >
            Cancel
          </Button>
          <Button
            size="small"
            color="dark"
            onClick={onConfirm}
            disabled={isRequesting}
            data-testid="request-underwriting-confirm"
          >
            Request underwriting
          </Button>
        </Fragment>
      }
    >
      <Typography variant="body2" css={tw`mb-3 text-shades-500`}>
        This sends {customerName} to the lender for an underwriting decision.
      </Typography>
      <Typography variant="body2" css={tw`mb-4 text-shades-500`}>
        Current status: <strong>{currentStatusLabel}</strong>
      </Typography>
    </CommonModal>
  )
}

export const RequestUnderwritingModal = memo(RequestUnderwritingModalComponent)
